/**
 * Secilen video dosyasinin gercekten saglam ve oynatilabilir olup olmadigini
 * kontrol eder. Iki asama var, biri digerinin kacirdigini yakaliyor:
 *
 * 1) Kutu taramasi (bagimsiz, ~1 ms): MP4 (ISO BMFF) ust seviye kutu
 *    basliklari okunur. Dosyanin yarim inmis olmasi en sik rastlanan
 *    bozulma; kutunun ilan ettigi boyut dosya sonunu asiyorsa kesin kanit.
 *    Dosyanin tamamini okumaz, sadece her kutunun 16 byte'lik basligini.
 *    ISO BMFF (mp4/mov) olmayan dosyalarda bu asama atlanir.
 *
 * 2) Tarayici decode probu: dosya gizli bir <video>'ya baglanip metadata
 *    okunur, sonra sonuna dogru atlanip oradaki kare gercekten cozulur.
 *    Oynatici (video.js / artplayer) ayni decoder uzerinde calistigi icin
 *    bu asama "bizim oynaticimizda acilir mi" sorusunu yanitliyor.
 *
 * Burada FORMAT POLITIKASI UYGULANMAZ: hangi formatin kabul edildigine
 * icerik tipinin accept degeri (backend'den geliyor) ve sunucunun kendi
 * dogrulamasi karar verir. Buradaki tek soru "video bozuk mu, oynuyor mu".
 *
 * mp4box (ve benzeri wasm parser'lar) denendi ve cikarildi: moov tablolari
 * bozulmus dosyalarda tarayicinin demuxer'indan daha TOLERANSLI davranip
 * hepsini "saglam" dedi, yani ek tespit getirmedi.
 */

const VIDEO_FILE_ERRORS = Object.freeze({
  // Tarayici dosyayi hic acamiyor: container/codec desteklenmiyor
  // (HEVC, ProRes, avi, wmv...) ya da dosya zaten video degil.
  UNSUPPORTED: "unsupported",
  // Dosya eksik: yarim inmis / yarim kopyalanmis.
  TRUNCATED: "truncated",
  // Yapisi bozuk: kutular ya da moov tablolari ayristirilamiyor.
  CORRUPT: "corrupt",
  // Ses var, goruntu yok.
  NO_VIDEO_TRACK: "no_video_track",
});

const METADATA_TIMEOUT = 20000;
// Sona atlayip kareyi cozme adiminin suresi. Zaman asimi SAGLAM kabul
// edilir: yavaslik yuzunden saglam dosyaya "bozuk" demek, bozuk dosyayi
// kacirmaktan daha kotu.
const DECODE_PROBE_TIMEOUT = 8000;

/**
 * @param {File} file
 * @returns {Promise<{valid: boolean, reason?: string, detail?: string, duration?: number, width?: number, height?: number}>}
 */
export async function inspectVideoFile(file) {
  if (!file || !file.size) {
    return { valid: false, reason: VIDEO_FILE_ERRORS.CORRUPT, detail: "bos dosya" };
  }

  const structure = await inspectContainerStructure(file);
  if (!structure.valid) return structure;

  return probePlayback(file);
}

/* ------------------------------------------------------------------ */
/* 1) Kutu taramasi                                                    */
/* ------------------------------------------------------------------ */

async function readBytes(blob) {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  // Eski WebView'lar icin (uygulama React Native WebView icinde de calisiyor)
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/** Kutu tipi 4 ham byte; bozuk dosyada okunamayan karakter geliyor. */
function printable(value) {
  return String(value).replace(/[^\x20-\x7E]/g, "?");
}

/** Ust seviye MP4 kutularini sadece basliklarini okuyarak dolasir. */
async function readBoxHeaders(file) {
  const boxes = [];
  const total = file.size;
  let offset = 0;

  // Guard: bozuk dosyada sonsuz donguye girmeyi engeller.
  for (let i = 0; i < 64 && offset + 8 <= total; i++) {
    let header;
    try {
      header = new DataView(await readBytes(file.slice(offset, offset + 16)));
    } catch (error) {
      return { boxes, unreadable: true };
    }
    if (header.byteLength < 8) break;

    const type = String.fromCharCode(
      header.getUint8(4), header.getUint8(5), header.getUint8(6), header.getUint8(7)
    );
    let size = header.getUint32(0);
    let headerSize = 8;

    if (size === 1) {
      // 64-bit boyut: ust 32 bit pratikte 0, Number guvenli araliktan tasmiyor.
      if (header.byteLength < 16) return { boxes, invalidHeader: true };
      size = header.getUint32(8) * 4294967296 + header.getUint32(12);
      headerSize = 16;
    } else if (size === 0) {
      // Son kutu: dosya sonuna kadar uzaniyor demek.
      boxes.push({ type, start: offset, end: total });
      break;
    }

    if (size < headerSize) return { boxes, invalidHeader: true };

    boxes.push({ type, start: offset, end: offset + size });
    offset += size;
  }

  return { boxes };
}

async function inspectContainerStructure(file) {
  const { boxes, unreadable, invalidHeader } = await readBoxHeaders(file);

  if (unreadable) {
    return { valid: false, reason: VIDEO_FILE_ERRORS.CORRUPT, detail: "dosya okunamadi" };
  }

  const types = boxes.map((box) => box.type);
  // ftyp normalde ilk kutudur; bazi dosyalar basina free/skip/wide koyuyor.
  // Yoksa dosya ISO BMFF (mp4/mov) degil: asagidaki kutu kontrolleri o
  // dosyaya uygulanamaz, bu asama hicbir sey soylemeden geciyor; karari
  // oynatilabilirlige bakan asama 2 veriyor.
  if (!types.slice(0, 4).includes("ftyp")) {
    return { valid: true };
  }

  // Buradan sonrasi yalnizca ISO BMFF icin gecerli.
  if (invalidHeader) {
    return { valid: false, reason: VIDEO_FILE_ERRORS.CORRUPT, detail: "gecersiz kutu basligi" };
  }

  const overflowing = boxes.find((box) => box.end > file.size);
  if (overflowing) {
    const missing = overflowing.end - file.size;
    return {
      valid: false,
      reason: VIDEO_FILE_ERRORS.TRUNCATED,
      detail: `${printable(overflowing.type)} kutusu ${missing} byte eksik`,
    };
  }

  if (!types.includes("moov")) {
    return {
      valid: false,
      reason: VIDEO_FILE_ERRORS.TRUNCATED,
      detail: `moov yok (kutular: ${printable(types.join(","))})`,
    };
  }

  return { valid: true };
}

/* ------------------------------------------------------------------ */
/* 2) Tarayici decode probu                                            */
/* ------------------------------------------------------------------ */

function probePlayback(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    let timer = null;
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Handler'lari once bosalt: asagidaki load() bos src ile yeni bir
      // error event'i tetikleyebiliyor.
      video.onloadedmetadata = null;
      video.onerror = null;
      video.onseeked = null;
      video.removeAttribute("src");
      video.load(); // suren network/decode isini durdurur
      URL.revokeObjectURL(objectUrl);
      resolve(result);
    };

    const fail = (reason, detail) => finish({ valid: false, reason, detail });

    timer = setTimeout(
      () => fail(VIDEO_FILE_ERRORS.CORRUPT, "metadata zaman asimi"),
      METADATA_TIMEOUT
    );

    video.onerror = () => {
      // MEDIA_ERR_SRC_NOT_SUPPORTED (4): container/codec acilmiyor.
      // MEDIA_ERR_DECODE (3): format taniniyor, veri bozuk.
      const code = video.error && video.error.code;
      fail(
        code === 4 ? VIDEO_FILE_ERRORS.UNSUPPORTED : VIDEO_FILE_ERRORS.CORRUPT,
        `MediaError ${code}`
      );
    };

    video.onloadedmetadata = () => {
      const { duration, videoWidth, videoHeight } = video;

      // Bozuk/yarim dosyada duration 0 veya NaN gelir; Infinity ise suresi
      // hic yazilmamis (kaydi kesilmis) dosya demek.
      if (!duration || !Number.isFinite(duration)) {
        fail(VIDEO_FILE_ERRORS.CORRUPT, `gecersiz sure: ${duration}`);
        return;
      }
      if (!videoWidth || !videoHeight) {
        fail(VIDEO_FILE_ERRORS.NO_VIDEO_TRACK, "goruntu boyutu yok");
        return;
      }

      const metadata = { valid: true, duration, width: videoWidth, height: videoHeight };

      // Sona dogru atlayip oradaki kareyi cozebiliyor muyuz?
      clearTimeout(timer);
      timer = setTimeout(() => finish(metadata), DECODE_PROBE_TIMEOUT);
      video.onseeked = () => finish(metadata);
      video.currentTime = Math.max(0, duration * 0.95);
    };

    video.preload = "metadata";
    video.muted = true;
    video.src = objectUrl;
  });
}

/* ------------------------------------------------------------------ */

/**
 * inspectVideoFile sonucundaki hata sebebini ceviri anahtarina donusturur.
 * @param {string} reason
 * @returns {string}
 */
export function videoFileErrorTranslationKey(reason) {
  if (reason === VIDEO_FILE_ERRORS.UNSUPPORTED) {
    return "general.video_format_not_supported";
  }
  if (reason === VIDEO_FILE_ERRORS.TRUNCATED) {
    return "general.video_file_truncated";
  }
  if (reason === VIDEO_FILE_ERRORS.NO_VIDEO_TRACK) {
    return "general.video_has_no_video_track";
  }
  return "general.video_file_corrupt";
}
